import { Box, Typography } from "@mui/material";
import { calculateFinalOneTimeRepaymentAmount } from "Charts/Utils/PropertyPurchaseChartUtils";
import { BlDataTable } from "Components/_Shared/DataTable/BlDataTable";
import { CurrencyCode } from "Infrastructure/Models/CurrencyCodes";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { formatCurrency } from "Infrastructure/Utils/CurrencyUtils";

type Props = {
  readonly originalLoanDuration: number;
  readonly loanPaymentAmount: number;
  readonly loanInterestRate: number;
  readonly loanAmount: number;
};

export const MortgageComparisonTable: React.FC<Props> = ({ originalLoanDuration, loanPaymentAmount, loanAmount, loanInterestRate }) => {
  const { t } = useResource();

  const loanTotalAmount = loanPaymentAmount * (originalLoanDuration * 12);

  const earlyRepaymentLoanDuration = originalLoanDuration - 5;
  const earlyRepaymentTotalAmount = loanPaymentAmount * (earlyRepaymentLoanDuration * 12);

  return (
    <BlDataTable
      condensed
      isPaginationDisabled
      rowEntityID="variant"
      rows={[
        {
          data: {
            variant: "Původní splacení",
            loanDuration: originalLoanDuration,
            loanTotalAmount,
            savings: 0,
            oneTimeAmount: 0,
          },
        },
        {
          data: {
            variant: "Předčasné",
            loanDuration: earlyRepaymentLoanDuration,
            loanTotalAmount: earlyRepaymentTotalAmount,
            savings: loanTotalAmount - earlyRepaymentTotalAmount,
            oneTimeAmount: calculateFinalOneTimeRepaymentAmount({
              durationInYears: earlyRepaymentLoanDuration,
              loanAmount,
              monthlyPaymentAmount: loanPaymentAmount,
              loanInterestRate,
            }),
          },
        },
      ]}
      columns={[
        {
          id: "variant",
          label: t(Resources.AnalysisDetail.Goals.MorgageComparisonTable.Variant),
        },
        {
          id: "loanDuration",
          label: t(Resources.AnalysisDetail.Goals.MorgageComparisonTable.LoanDuration),
        },
        {
          id: "loanTotalAmount",
          label: t(Resources.AnalysisDetail.Goals.MorgageComparisonTable.TotalLoanAmount),
        },
        {
          id: "oneTimeAmount",
          label: t(Resources.AnalysisDetail.Goals.MorgageComparisonTable.OneTimeAmount),
        },
        {
          id: "savings",
          label: t(Resources.AnalysisDetail.Goals.MorgageComparisonTable.Savings),
        },
      ]}
      getHeaderCell={(column) => (
        <Box p={Spacing.XS}>
          <Typography lineHeight={1.3} variant="caption" fontWeight={500} color="secondary">
            {column.label}
          </Typography>
        </Box>
      )}
      getCell={(id, data) => {
        switch (id) {
          case "variant":
            return (
              <Box pl={Spacing.XS} pb={Spacing.M} pt={Spacing.XS}>
                <Typography variant="caption" fontWeight={500}>
                  {data.variant}
                </Typography>
              </Box>
            );
          case "loanDuration":
            return (
              <Box pl={Spacing.XS} pb={Spacing.M} pt={Spacing.XS}>
                <Typography variant="caption">
                  {data.loanDuration} {t(Resources.Common.Years)}
                </Typography>
              </Box>
            );
          case "loanTotalAmount":
            return (
              <Box pl={Spacing.XS} pb={Spacing.M} pt={Spacing.XS}>
                <Typography variant="caption">{formatCurrency(data.loanTotalAmount, CurrencyCode.CZK, 0)}</Typography>
              </Box>
            );
          case "oneTimeAmount":
            return (
              <Box pl={Spacing.XS} pb={Spacing.M} pt={Spacing.XS}>
                {data.oneTimeAmount > 0 ? (
                  <Typography variant="caption">{formatCurrency(data.oneTimeAmount, CurrencyCode.CZK, 0)}</Typography>
                ) : (
                  <Typography variant="caption">-</Typography>
                )}
              </Box>
            );
          case "savings":
            return (
              <Box pl={Spacing.XS} pb={Spacing.M} pt={Spacing.XS}>
                {data.savings > 0 ? (
                  <Typography color="success" variant="caption">
                    {formatCurrency(data.savings, CurrencyCode.CZK, 0)}
                  </Typography>
                ) : (
                  <Typography variant="caption">-</Typography>
                )}
              </Box>
            );
          default:
            return <Box />;
        }
      }}
    />
  );
};
